import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails,
    Tabs, Tab, Table, TableBody, TableCell, TableRow, TableContainer,
    Tooltip, Skeleton, Dialog, IconButton
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Cancel as CancelIcon,
    Close as CloseIcon,
    ContentCopy as ContentCopyIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
} from '@mui/icons-material';
import { getAllLimitedCards, getAllArchetypes, getDeck } from '../../services/tracker-limited-data';
import { InkColorIcons } from './InkColorIcon';
import DeckVisual from './DeckVisual';
import DeckPreviewModal from './DeckPreviewModal';

// ─── Config ──────────────────────────────────────────────────────────────────

const FAVORABILITY = {
    very_favored:   { label: 'Bardzo faworyzowany',   short: '++',  miniLabel: 'Faworyt',     color: '#10b981', bg: 'rgba(16,185,129,0.15)',  border: 'rgba(16,185,129,0.3)',  bar: 75 },
    favored:        { label: 'Faworyzowany',           short: '+',   miniLabel: 'Lekko +',     color: '#84cc16', bg: 'rgba(132,204,22,0.15)', border: 'rgba(132,204,22,0.3)',  bar: 62 },
    even:           { label: 'Wyrównany',              short: '=',   miniLabel: 'Równy',       color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)',  bar: 50 },
    unfavored:      { label: 'Niefaworyzowany',        short: '−',   miniLabel: 'Lekko −',     color: '#f97316', bg: 'rgba(249,115,22,0.15)', border: 'rgba(249,115,22,0.3)',  bar: 38 },
    very_unfavored: { label: 'Bardzo niefaworyzowany', short: '−−',  miniLabel: 'Niekorzystny',color: '#ef4444', bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.3)',  bar: 25 },
};

const DIFFICULTY = {
    low:    { label: 'Łatwy',  color: '#10b981', stars: 1 },
    medium: { label: 'Średni', color: '#f59e0b', stars: 2 },
    high:   { label: 'Trudny', color: '#ef4444', stars: 3 },
};

const PLAYSTYLE = {
    aggro:    { bg: 'rgba(239,68,68,0.15)',   color: '#ef4444', border: 'rgba(239,68,68,0.3)' },
    midrange: { bg: 'rgba(245,158,11,0.15)',  color: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
    control:  { bg: 'rgba(99,102,241,0.15)',  color: '#818cf8', border: 'rgba(99,102,241,0.3)' },
    combo:    { bg: 'rgba(168,85,247,0.15)',  color: '#c084fc', border: 'rgba(168,85,247,0.3)' },
    tempo:    { bg: 'rgba(20,184,166,0.15)',  color: '#2dd4bf', border: 'rgba(20,184,166,0.3)' },
};

const hasMulliganContent = (side) =>
    (side?.priorities?.length > 0) || (side?.example_hands?.length > 0);

const PLAY_LINE_SIDE = {
    otp: { label: 'OTP', color: '#34d399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)' },
    otd: { label: 'OTD', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)' },
};

const CARD_W = { xs: 44, sm: 54 };
const CARD_H = { xs: 62, sm: 76 };
const CARD_W_MD = { xs: 62, sm: 76 };
const CARD_H_MD = { xs: 87, sm: 107 };

const sectionBox = {
    background: 'rgba(17,24,39,0.7)',
    border: '1px solid rgba(76,29,149,0.2)',
    borderRadius: '12px',
    p: { xs: 2, sm: 2.5 },
    mb: 2.5,
};

// ─── Card lookup hook ─────────────────────────────────────────────────────────

function useCardLookup() {
    const [lookup, setLookup] = useState({});
    const [ready, setReady] = useState(false);

    useEffect(() => {
        getAllLimitedCards()
            .then((cards) => {
                const map = {};
                (cards || []).forEach((card) => {
                    const name = card.name || '';
                    const subtitle = card.subtitle || '';
                    const fullName = subtitle ? `${name} - ${subtitle}` : name;

                    // pick best thumbnail and detail url from variants
                    const variant = card.variants?.[0] || {};
                    const thumbnail =
                        variant.thumbnail_image_url ||
                        variant.thumb_image_url ||
                        variant.card_image_url ||
                        variant.detail_image_url ||
                        card.image_urls?.[0]?.url ||
                        null;
                    const detail =
                        variant.detail_image_url ||
                        variant.card_image_url ||
                        card.image_urls?.[0]?.url ||
                        null;

                    const entry = { thumbnail, detail, name: fullName, cost: card.ink_cost };

                    [fullName, name].forEach((n) => {
                        const key = n.toLowerCase().trim();
                        if (key && !map[key]) map[key] = entry;
                    });
                });
                setLookup(map);
            })
            .catch(() => setLookup({}))
            .finally(() => setReady(true));
    }, []);

    const get = useCallback((cardName) => {
        if (!cardName) return null;
        return lookup[cardName.toLowerCase().trim()] || null;
    }, [lookup]);

    return { get, ready };
}

// ─── Archetype lookup hook ────────────────────────────────────────────────────

function useArchetypeLookup(trackerFilters = {}) {
    const [lookup, setLookup] = useState({});
    const format = trackerFilters?.format || null;
    const setId = trackerFilters?.setId || null;
    useEffect(() => {
        getAllArchetypes({ format, setId })
            .then((data) => {
                const arr = Array.isArray(data) ? data : (data?.archetypes || []);
                const map = {};
                arr.forEach((a) => {
                    const id = a.id || a.archetype_id || '';
                    if (id) map[id] = { name: a.name || a.display_name || id, ink_colors: a.ink_colors || [] };
                });
                setLookup(map);
            })
            .catch(() => {});
    }, [format, setId]);
    return lookup;
}

// ─── Primitives ───────────────────────────────────────────────────────────────

function SectionHeader({ children, id, action }) {
    return (
        <Box id={id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ width: 3, height: 20, borderRadius: '2px', bgcolor: '#7c3aed', flexShrink: 0 }} />
            <Typography sx={{
                color: '#e2d9ff', fontWeight: 700, fontSize: '0.97rem',
                textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0
            }}>
                {children}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(76,29,149,0.2)' }} />
            {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Box>
    );
}

function CardImage({ name, cardData, width, height, dimmed, showX, onClick }) {
    if (!cardData?.thumbnail) {
        return (
            <Box onClick={onClick} sx={{
                width, height, borderRadius: '6px', flexShrink: 0,
                bgcolor: dimmed ? 'rgba(239,68,68,0.08)' : 'rgba(124,58,237,0.1)',
                border: `1.5px solid ${dimmed ? 'rgba(239,68,68,0.3)' : 'rgba(124,58,237,0.25)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                p: 0.5, position: 'relative', opacity: dimmed ? 0.45 : 1,
                cursor: onClick ? 'pointer' : 'default',
            }}>
                <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.7)', textAlign: 'center', lineHeight: 1.25, wordBreak: 'break-word' }}>
                    {name}
                </Typography>
                {showX && <CancelIcon sx={{ position: 'absolute', inset: 0, m: 'auto', color: '#ef4444', fontSize: 20 }} />}
            </Box>
        );
    }

    return (
        <Tooltip
            arrow
            placement="top"
            title={
                <Box sx={{ p: 0.5 }}>
                    <Box component="img" src={cardData.detail} alt={name}
                        sx={{ width: 180, borderRadius: '8px', display: 'block' }} />
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(196,181,253,0.9)', mt: 0.5, textAlign: 'center' }}>
                        {name}
                    </Typography>
                </Box>
            }
            componentsProps={{ tooltip: { sx: { bgcolor: 'rgba(17,24,39,0.97)', border: '1px solid rgba(76,29,149,0.4)', p: 0.75, maxWidth: 220 } } }}
        >
            <Box onClick={onClick} sx={{ position: 'relative', flexShrink: 0, cursor: onClick ? 'pointer' : 'default', width, height }}>
                <Box
                    component="img"
                    src={cardData.thumbnail}
                    alt={name}
                    sx={{
                        width, height, borderRadius: '6px', objectFit: 'cover', display: 'block',
                        border: `1.5px solid ${dimmed ? 'rgba(239,68,68,0.4)' : 'rgba(124,58,237,0.35)'}`,
                        filter: dimmed ? 'brightness(0.3) saturate(0.5)' : 'none',
                        transition: 'transform 0.15s, border-color 0.15s',
                        '&:hover': { transform: 'scale(1.04)', borderColor: dimmed ? 'rgba(239,68,68,0.7)' : 'rgba(124,58,237,0.7)' }
                    }}
                />
                {showX && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}>
                        <CancelIcon sx={{ color: '#ef4444', fontSize: { xs: 22, sm: 26 }, filter: 'drop-shadow(0 0 5px rgba(0,0,0,0.9))' }} />
                    </Box>
                )}
            </Box>
        </Tooltip>
    );
}

function CardBadge({ name, count, threat, getCard }) {
    const card = getCard(name);
    return (
        <Tooltip
            arrow
            placement="top"
            title={card?.detail ? (
                <Box sx={{ p: 0.5 }}>
                    <Box component="img" src={card.detail} alt={name} sx={{ width: 160, borderRadius: '6px', display: 'block' }} />
                </Box>
            ) : name}
            componentsProps={{ tooltip: { sx: { bgcolor: 'rgba(17,24,39,0.97)', border: '1px solid rgba(76,29,149,0.4)', p: card?.detail ? 0.75 : undefined } } }}
        >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, cursor: 'default' }}>
                {count != null && (
                    <Typography sx={{
                        fontSize: '0.68rem', fontWeight: 700, color: '#c4b5fd',
                        bgcolor: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)',
                        borderRadius: '4px', px: 0.5, lineHeight: 1.5, flexShrink: 0
                    }}>{count}x</Typography>
                )}
                {card?.thumbnail ? (
                    <Box component="img" src={card.thumbnail} alt={name}
                        sx={{ height: 24, width: 17, borderRadius: '3px', objectFit: 'cover', verticalAlign: 'middle', border: `1px solid ${threat ? 'rgba(239,68,68,0.4)' : 'rgba(124,58,237,0.3)'}` }} />
                ) : null}
                <Typography sx={{
                    fontSize: '0.78rem', fontWeight: 500,
                    color: threat ? '#f87171' : 'rgba(255,255,255,0.9)',
                    bgcolor: threat ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${threat ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '5px', px: 0.75, py: 0.2, lineHeight: 1.4
                }}>{name}</Typography>
            </Box>
        </Tooltip>
    );
}

// ─── Deck Dashboard ───────────────────────────────────────────────────────────

function useDeck(deckId) {
    const [deck, setDeck] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!deckId) { setDeck(null); return; }
        setLoading(true);
        getDeck(deckId)
            .then(setDeck)
            .catch(() => setDeck(null))
            .finally(() => setLoading(false));
    }, [deckId]);
    return { deck, loading };
}

// ─── TL;DR / Guide Dashboard ──────────────────────────────────────────────────

const SNAPSHOT_COLUMNS = [
    { key: 'very_unfavored', label: 'Niefaworyz.' },
    { key: 'unfavored',      label: 'Lekko niefaworyz.' },
    { key: 'even',           label: 'Wyrównany' },
    { key: 'favored',        label: 'Lekko faworyz.' },
    { key: 'very_favored',   label: 'Faworyz.' },
];

function MatchupSnapshotTable({ items, archetypeLookup }) {
    const navigate = useNavigate();
    if (!items?.length) return null;

    const byFav = {};
    SNAPSHOT_COLUMNS.forEach((c) => { byFav[c.key] = []; });
    items.forEach((m) => {
        if (byFav[m.favorability]) byFav[m.favorability].push(m);
    });

    const anyEntries = SNAPSHOT_COLUMNS.some((c) => byFav[c.key].length > 0);
    if (!anyEntries) return null;

    return (
        <Box>
            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(196,181,253,0.35)', textTransform: 'uppercase', letterSpacing: '0.09em', mb: 0.75 }}>
                Matchupy — szybki przegląd
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: { xs: 0.4, sm: 0.75 }, alignItems: 'start' }}>
                {SNAPSHOT_COLUMNS.map((col) => {
                    const cfg = FAVORABILITY[col.key];
                    const entries = byFav[col.key];
                    const isEmpty = entries.length === 0;
                    return (
                        <Box key={col.key} sx={{
                            ...(isEmpty ? {
                                py: 0.5,
                                px: 0.25,
                                opacity: 0.35,
                            } : {
                                bgcolor: `${cfg.color}0a`,
                                border: `1px solid ${cfg.border}`,
                                borderRadius: '8px',
                                p: { xs: 0.5, sm: 0.75 },
                            }),
                            minWidth: 0,
                        }}>
                            <Typography sx={{
                                fontSize: isEmpty ? '0.58rem' : { xs: '0.6rem', sm: '0.65rem' },
                                color: cfg.color,
                                fontWeight: isEmpty ? 500 : 700,
                                textAlign: 'center',
                                mb: isEmpty ? 0 : 0.6,
                                lineHeight: 1.2,
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                                {col.label}
                            </Typography>
                            {!isEmpty && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                {entries.map((m) => {
                                    const archInfo = archetypeLookup[m.archetype_id] || {};
                                    const inkColors = m.ink_colors || archInfo.ink_colors || [];
                                    const name = m.archetype_name || archInfo.name || m.archetype_id;
                                    const canNav = Boolean(m.archetype_id);
                                    return (
                                        <Box
                                            key={m.archetype_id || name}
                                            onClick={canNav ? () => navigate(`/archetype/${m.archetype_id}`) : undefined}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 0.4,
                                                bgcolor: 'rgba(0,0,0,0.25)',
                                                border: `1px solid ${cfg.border}`,
                                                borderRadius: '6px',
                                                px: 0.6, py: 0.35,
                                                cursor: canNav ? 'pointer' : 'default',
                                                '&:hover': canNav ? { bgcolor: `${cfg.color}18`, borderColor: cfg.color } : {},
                                                transition: 'all 0.12s',
                                                minWidth: 0,
                                            }}
                                        >
                                            {inkColors.length > 0 && (
                                                <InkColorIcons colors={inkColors} size={13} gap={0.2} tooltip={false} sx={{ flexShrink: 0 }} />
                                            )}
                                            <Typography sx={{
                                                fontSize: { xs: '0.64rem', sm: '0.7rem' },
                                                color: 'rgba(255,255,255,0.88)',
                                                fontWeight: 500,
                                                lineHeight: 1.25,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                minWidth: 0,
                                            }}>
                                                {name}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// Compact portrait thumbnail for deck one-pager
function DeckCardThumb({ card }) {
    const imageUrl = card?.image?.thumbnail || card?.image?.detail;
    const detailUrl = card?.image?.detail || card?.image?.thumbnail;
    const qty = card?.quantity || 1;
    const W = { xs: 62, sm: 72 };
    const H = { xs: 87, sm: 101 };

    return (
        <Tooltip
            arrow
            placement="top"
            title={detailUrl ? (
                <Box component="img" src={detailUrl} alt={card?.name || ''}
                    sx={{ width: 180, borderRadius: '8px', display: 'block' }} />
            ) : (card?.name || '')}
            componentsProps={{ tooltip: { sx: { bgcolor: 'rgba(17,24,39,0.97)', border: '1px solid rgba(76,29,149,0.4)', p: detailUrl ? 0.5 : undefined } } }}
        >
            <Box sx={{ position: 'relative', flexShrink: 0, width: W, height: H }}>
                {imageUrl ? (
                    <Box component="img" src={imageUrl} alt={card?.name || ''} sx={{
                        width: W, height: H, borderRadius: '6px', objectFit: 'cover', display: 'block',
                        border: '1px solid rgba(76,29,149,0.4)',
                        transition: 'transform 0.12s, border-color 0.12s',
                        '&:hover': { transform: 'scale(1.06)', borderColor: 'rgba(124,58,237,0.7)', zIndex: 2 },
                    }} />
                ) : (
                    <Box sx={{
                        width: W, height: H, borderRadius: '6px', border: '1px solid rgba(76,29,149,0.3)',
                        bgcolor: 'rgba(124,58,237,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Typography sx={{ fontSize: '0.52rem', color: 'rgba(196,181,253,0.5)', textAlign: 'center', p: 0.25, lineHeight: 1.2, wordBreak: 'break-word' }}>
                            {card?.name || '?'}
                        </Typography>
                    </Box>
                )}
                {qty > 1 && (
                    <Box sx={{
                        position: 'absolute', top: 3, right: 3, width: 17, height: 17, borderRadius: '50%',
                        bgcolor: 'rgba(8,8,18,0.92)', border: '1.5px solid rgba(124,58,237,0.7)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
                    }}>
                        <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.55rem', lineHeight: 1 }}>{qty}</Typography>
                    </Box>
                )}
            </Box>
        </Tooltip>
    );
}

// Large key card with name label for TL;DR highlight section
function KeyCardThumb({ cardName, cardData, compact = false, threat = false }) {
    const W = compact ? { xs: 62, sm: 72 } : { xs: 82, sm: 96 };
    const H = compact ? { xs: 87, sm: 101 } : { xs: 115, sm: 135 };
    const borderColor = threat ? 'rgba(239,68,68,0.45)' : 'rgba(124,58,237,0.4)';
    const hoverBorderColor = threat ? 'rgba(239,68,68,0.85)' : 'rgba(124,58,237,0.8)';
    const labelColor = threat ? '#f87171' : 'rgba(196,181,253,0.75)';
    const shortName = cardName?.split(' - ')?.[0] || cardName || '';

    return (
        <Tooltip
            arrow
            placement="top"
            title={cardData?.detail ? (
                <Box component="img" src={cardData.detail} alt={cardName}
                    sx={{ width: 200, borderRadius: '8px', display: 'block' }} />
            ) : cardName}
            componentsProps={{ tooltip: { sx: { bgcolor: 'rgba(17,24,39,0.97)', border: '1px solid rgba(76,29,149,0.4)', p: cardData?.detail ? 0.5 : undefined } } }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
                {cardData?.thumbnail ? (
                    <Box component="img" src={cardData.thumbnail} alt={cardName} sx={{
                        width: W, height: H, borderRadius: '7px', objectFit: 'cover', display: 'block',
                        border: `1.5px solid ${borderColor}`,
                        transition: 'transform 0.13s, border-color 0.13s',
                        '&:hover': { transform: 'scale(1.05)', borderColor: hoverBorderColor },
                    }} />
                ) : (
                    <Box sx={{
                        width: W, height: H, borderRadius: '7px',
                        bgcolor: threat ? 'rgba(239,68,68,0.08)' : 'rgba(124,58,237,0.1)',
                        border: `1.5px solid ${borderColor}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5
                    }}>
                        <Typography sx={{ fontSize: '0.6rem', color: labelColor, textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-word' }}>
                            {shortName}
                        </Typography>
                    </Box>
                )}
                <Typography sx={{
                    fontSize: compact ? '0.58rem' : '0.62rem', color: labelColor, fontWeight: 600,
                    textAlign: 'center', lineHeight: 1.2, maxWidth: W,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {shortName}
                </Typography>
            </Box>
        </Tooltip>
    );
}

function TldrSection({ tldr, matchups, archetypeLookup, deckId, getCard }) {
    const navigate = useNavigate();
    const { deck, loading: deckLoading } = useDeck(deckId);
    const [copied, setCopied] = useState(false);

    if (!tldr) return null;

    const diff = DIFFICULTY[tldr.difficulty];
    const snapshotItems = (matchups?.length > 0)
        ? matchups.map((m) => ({ archetype_id: m.archetype_id, archetype_name: m.archetype_name, favorability: m.favorability, ink_colors: m.ink_colors }))
        : (tldr.matchup_snapshot || []);

    // Sorted deck cards (by cost)
    const rawCards = Array.isArray(deck?.deckVisual?.cards) ? deck.deckVisual.cards : [];
    const deckCards = [...rawCards].sort((a, b) => {
        const ca = a.cost ?? 99, cb = b.cost ?? 99;
        return ca !== cb ? ca - cb : (a.sortNumber || 0) - (b.sortNumber || 0);
    });
    const totalCards = rawCards.reduce((s, c) => s + (c.quantity || 1), 0);

    const handleCopy = () => {
        if (!deck?.cards) return;
        navigator.clipboard.writeText(deck.cards);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    // Matchups sorted favored→unfavored
    const FAV_ORDER = { very_favored: 0, favored: 1, even: 2, unfavored: 3, very_unfavored: 4 };
    const sortedMatchups = [...snapshotItems].sort((a, b) => (FAV_ORDER[a.favorability] ?? 9) - (FAV_ORDER[b.favorability] ?? 9));

    // Key cards (author-selected)
    const keyCardNames = tldr.key_cards || [];

    const hasMatchups = sortedMatchups.length > 0;
    const hasKeyCards = keyCardNames.length > 0;
    const hasRight = hasKeyCards || hasMatchups;
    const hasDeck = Boolean(deckId);

    const divBorder = { borderBottom: '1px solid rgba(76,29,149,0.14)' };
    const panelLabel = { fontSize: '0.65rem', color: 'rgba(196,181,253,0.28)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, mb: 0.8 };

    return (
        <Box id="guide-tldr" sx={{ background: 'rgba(17,24,39,0.72)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: '14px', mb: 3, overflow: 'hidden' }}>

            {/* ── Header row ── */}
            <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5, pb: 1.25, borderBottom: '1px solid rgba(76,29,149,0.16)' }}>
                {tldr.summary && (
                    <Typography sx={{
                        fontSize: '0.95rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.7,
                        mb: (diff || tldr.playstyle_tags?.length) ? 1.25 : 0,
                        borderLeft: '3px solid rgba(124,58,237,0.45)', pl: 1.5,
                    }}>
                        {tldr.summary}
                    </Typography>
                )}
                {(diff || tldr.playstyle_tags?.length > 0) && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        {diff && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                <Typography sx={{ fontSize: '0.65rem', color: 'rgba(196,181,253,0.45)', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
                                    Poziom:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.9, py: 0.35, bgcolor: `${diff.color}18`, border: `1px solid ${diff.color}40`, borderRadius: '7px' }}>
                                    <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
                                        {[1, 2, 3].map((s) => <Box key={s} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s <= diff.stars ? diff.color : 'rgba(255,255,255,0.12)' }} />)}
                                    </Box>
                                    <Typography sx={{ fontSize: '0.72rem', color: diff.color, fontWeight: 700 }}>{diff.label}</Typography>
                                </Box>
                            </Box>
                        )}
                        {tldr.playstyle_tags?.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                <Typography sx={{ fontSize: '0.65rem', color: 'rgba(196,181,253,0.45)', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
                                    Styl:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {tldr.playstyle_tags.map((tag) => {
                                        const cfg = PLAYSTYLE[tag] || { bg: 'rgba(255,255,255,0.08)', color: 'white', border: 'rgba(255,255,255,0.15)' };
                                        return <Chip key={tag} label={tag} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontSize: '0.72rem', height: 22, fontWeight: 600, textTransform: 'capitalize', '& .MuiChip-label': { px: 0.75 } }} />;
                                    })}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* ── Main grid: deck (left) + key cards + matchups (right) ── */}
            {(hasDeck || hasRight) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: hasDeck && hasRight ? { xs: '1fr', md: '3fr 2fr' } : '1fr', alignItems: 'start' }}>

                    {/* ── Left: deck card grid ── */}
                    {hasDeck && (
                        <Box sx={{ borderRight: { md: hasRight ? '1px solid rgba(76,29,149,0.16)' : 'none' }, borderBottom: { xs: hasRight ? '1px solid rgba(76,29,149,0.16)' : 'none', md: 'none' } }}>
                            <Box sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ ...panelLabel, mb: 0 }}>Lista kart</Typography>
                                {!deckLoading && totalCards > 0 && (
                                    <Typography sx={{ fontSize: '0.56rem', color: 'rgba(196,181,253,0.2)' }}>{totalCards} kart</Typography>
                                )}
                                {deck?.cards && (
                                    <Tooltip title={copied ? 'Skopiowano!' : 'Kopiuj listę'} arrow>
                                        <Box onClick={handleCopy} sx={{ display: 'flex', alignItems: 'center', gap: 0.35, px: 0.75, py: 0.28, borderRadius: '5px', cursor: 'pointer', color: copied ? '#34d399' : 'rgba(196,181,253,0.4)', border: `1px solid ${copied ? 'rgba(52,211,153,0.3)' : 'rgba(76,29,149,0.25)'}`, '&:hover': { bgcolor: 'rgba(124,58,237,0.08)', color: '#c4b5fd' }, transition: 'all 0.15s', ml: 'auto' }}>
                                            <ContentCopyIcon sx={{ fontSize: 12 }} />
                                            <Typography sx={{ fontSize: '0.63rem' }}>Kopiuj</Typography>
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                            {deckLoading ? (
                                <Box sx={{ px: 1.5, pb: 1.5 }}>
                                    <Skeleton variant="rectangular" height={130} sx={{ borderRadius: '7px', bgcolor: 'rgba(124,58,237,0.07)' }} />
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: '4px', sm: '5px' }, px: 1.5, pb: 1.5, pt: 0.5 }}>
                                    {deckCards.map((card) => (
                                        <DeckCardThumb key={card.cardId || card.name} card={card} />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* ── Right: key cards + matchups ── */}
                    {hasRight && (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>

                            {hasKeyCards && (
                                <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25, ...(hasMatchups ? divBorder : {}) }}>
                                    <Typography sx={panelLabel}>Kluczowe karty</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: '8px', sm: '10px' } }}>
                                        {keyCardNames.map((cn) => (
                                            <KeyCardThumb key={cn} cardName={cn} cardData={getCard ? getCard(cn) : null} />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {hasMatchups && (
                                <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25 }}>
                                    <Typography sx={panelLabel}>Matchupy</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                        {sortedMatchups.map((m) => {
                                            const cfg = FAVORABILITY[m.favorability] || FAVORABILITY.even;
                                            const archInfo = archetypeLookup[m.archetype_id] || {};
                                            const inkColors = m.ink_colors || archInfo.ink_colors || [];
                                            const name = m.archetype_name || archInfo.name || m.archetype_id;
                                            const canNav = Boolean(m.archetype_id);
                                            return (
                                                <Box key={m.archetype_id || name}
                                                    onClick={canNav ? () => navigate(`/archetype/${m.archetype_id}`) : undefined}
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.35, borderRadius: '7px', bgcolor: `${cfg.color}0a`, border: `1px solid ${cfg.border}`, cursor: canNav ? 'pointer' : 'default', '&:hover': canNav ? { bgcolor: `${cfg.color}18` } : {}, transition: 'background 0.1s' }}
                                                >
                                                    {inkColors.length > 0 && <InkColorIcons colors={inkColors} size={13} gap={0.15} tooltip={false} sx={{ flexShrink: 0 }} />}
                                                    <Typography sx={{ flex: 1, fontSize: '0.73rem', color: 'rgba(255,255,255,0.85)', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.67rem', fontWeight: 800, color: cfg.color, flexShrink: 0, px: 0.5, py: 0.15, bgcolor: `${cfg.color}15`, borderRadius: '4px', border: `1px solid ${cfg.border}` }}>{cfg.short}</Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

// ─── Card Breakdown ───────────────────────────────────────────────────────────

const CARD_LIST_W = { xs: 64, sm: 80 };
const CARD_LIST_H = { xs: 90, sm: 112 };

function CardPreviewModal({ card, name, open, onClose }) {
    if (!card?.detail && !card?.thumbnail) return null;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs"
            PaperProps={{ sx: { bgcolor: 'rgba(17,24,39,0.97)', border: '1px solid rgba(76,29,149,0.4)', borderRadius: '14px', p: 0, overflow: 'hidden' } }}
            BackdropProps={{ sx: { backdropFilter: 'blur(4px)' } }}
        >
            <Box sx={{ position: 'relative' }}>
                <IconButton onClick={onClose} size="small" sx={{
                    position: 'absolute', top: 8, right: 8, zIndex: 10,
                    bgcolor: 'rgba(17,24,39,0.8)', color: 'rgba(196,181,253,0.8)',
                    '&:hover': { bgcolor: 'rgba(124,58,237,0.3)' }
                }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Box component="img" src={card.detail || card.thumbnail} alt={name}
                    sx={{ width: '100%', maxWidth: 320, display: 'block', borderRadius: '14px' }} />
            </Box>
        </Dialog>
    );
}

function CardBreakdownSection({ cardBreakdown, getCard }) {
    const [tab, setTab] = useState(0);
    const [preview, setPreview] = useState(null);
    if (!cardBreakdown?.length) return null;

    const activeGroup = cardBreakdown[tab];

    return (
        <Box sx={sectionBox}>
            <SectionHeader id="guide-cards">Opis kart</SectionHeader>
            {cardBreakdown.length > 1 && (
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
                    mb: 2, minHeight: 34,
                    '& .MuiTab-root': { minHeight: 34, py: 0, fontSize: '0.8rem', color: 'rgba(196,181,253,0.5)', textTransform: 'none', fontWeight: 500, px: 1.5 },
                    '& .Mui-selected': { color: '#c4b5fd', fontWeight: 700 },
                    '& .MuiTabs-indicator': { bgcolor: '#7c3aed', height: 2.5 },
                    borderBottom: '1px solid rgba(76,29,149,0.2)'
                }}>
                    {cardBreakdown.map((g, i) => <Tab key={i} label={g.group} />)}
                </Tabs>
            )}
            {cardBreakdown.length === 1 && (
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(196,181,253,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                    {activeGroup.group}
                </Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
                {activeGroup?.cards?.map((c, i) => {
                    const card = getCard(c.card_name);
                    return (
                        <Box key={i} sx={{
                            display: 'flex', gap: { xs: 1.25, sm: 1.75 }, alignItems: 'flex-start',
                            p: { xs: 1, sm: 1.25 },
                            bgcolor: 'rgba(124,58,237,0.05)',
                            border: '1px solid rgba(124,58,237,0.15)',
                            borderRadius: '10px',
                            transition: 'border-color 0.15s',
                            '&:hover': { borderColor: 'rgba(124,58,237,0.3)' }
                        }}>
                            {/* Card thumbnail – clickable for large preview */}
                            <Box onClick={() => card && setPreview({ card, name: c.card_name })} sx={{
                                position: 'relative', flexShrink: 0,
                                cursor: card ? 'pointer' : 'default',
                                '&:hover img': card ? { borderColor: 'rgba(124,58,237,0.7)', transform: 'scale(1.04)' } : {}
                            }}>
                                {card?.thumbnail ? (
                                    <Box component="img" src={card.thumbnail} alt={c.card_name} sx={{
                                        width: CARD_LIST_W, height: CARD_LIST_H,
                                        borderRadius: '7px', objectFit: 'cover', display: 'block',
                                        border: '1.5px solid rgba(124,58,237,0.35)',
                                        transition: 'transform 0.15s, border-color 0.15s',
                                    }} />
                                ) : (
                                    <Box sx={{
                                        width: CARD_LIST_W, height: CARD_LIST_H, borderRadius: '7px',
                                        bgcolor: 'rgba(124,58,237,0.1)', border: '1.5px solid rgba(124,58,237,0.25)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5
                                    }}>
                                        <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.6)', textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-word' }}>
                                            {c.card_name}
                                        </Typography>
                                    </Box>
                                )}
                                {c.count != null && (
                                    <Box sx={{
                                        position: 'absolute', top: -5, right: -5, zIndex: 2,
                                        bgcolor: '#7c3aed', color: 'white', borderRadius: '50%',
                                        width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '0.65rem', fontWeight: 700, border: '1.5px solid rgba(17,24,39,0.9)',
                                        lineHeight: 1
                                    }}>
                                        {c.count}
                                    </Box>
                                )}
                            </Box>

                            {/* Card info */}
                            <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap', mb: c.note ? 0.75 : 0 }}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#c4b5fd', lineHeight: 1.3 }}>
                                        {c.card_name}
                                    </Typography>
                                </Box>
                                {c.note && (
                                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.6 }}>
                                        {c.note}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {preview && (
                <CardPreviewModal
                    card={preview.card}
                    name={preview.name}
                    open={Boolean(preview)}
                    onClose={() => setPreview(null)}
                />
            )}
        </Box>
    );
}

// ─── Shared OTP/OTD Toggle ───────────────────────────────────────────────────

function OtpOtdToggle({ sides, active, onChange }) {
    if (sides.length < 2) return null;
    return (
        <Box sx={{ display: 'inline-flex', gap: 0, borderRadius: '8px', border: '1px solid rgba(76,29,149,0.3)', overflow: 'hidden', mb: 1.5 }}>
            {sides.map(({ label }, i) => (
                <Box key={label} onClick={() => onChange(i)} sx={{
                    px: 1.75, py: 0.55, cursor: 'pointer',
                    bgcolor: active === i ? 'rgba(124,58,237,0.35)' : 'transparent',
                    borderRight: i < sides.length - 1 ? '1px solid rgba(76,29,149,0.25)' : 'none',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: active === i ? 'rgba(124,58,237,0.35)' : 'rgba(124,58,237,0.12)' },
                }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: active === i ? 700 : 500, color: active === i ? '#c4b5fd' : 'rgba(196,181,253,0.45)', letterSpacing: '0.01em' }}>
                        {label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// ─── Gameplan ─────────────────────────────────────────────────────────────────

const TURN_CARD_W = 48;
const TURN_CARD_H = 67;

function TurnRow({ row, getCard, cardsColWidth }) {
    const cards = row.card_names?.filter(Boolean) || [];
    const cellBorder = '1px solid rgba(76,29,149,0.12)';
    return (
        <TableRow sx={{ '&:last-child td': { borderBottom: 'none' } }}>
            {/* Turn number */}
            <TableCell sx={{
                width: 34, py: 1, px: 1.25, borderBottom: cellBorder,
                color: '#7c3aed', fontWeight: 700, fontSize: '0.72rem',
                bgcolor: 'rgba(124,58,237,0.08)', borderRight: cellBorder,
                verticalAlign: 'middle', whiteSpace: 'nowrap',
            }}>T{row.turn}</TableCell>
            {/* Cards */}
            <TableCell sx={{
                py: 1, px: 1.25, borderBottom: cellBorder,
                borderRight: cellBorder, verticalAlign: 'middle',
                width: cardsColWidth, minWidth: cardsColWidth, maxWidth: cardsColWidth,
                whiteSpace: 'nowrap',
            }}>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', alignItems: 'center' }}>
                    {cards.length > 0 ? cards.map((cn, j) => {
                        const cd = getCard(cn);
                        return cd?.thumbnail ? (
                            <Tooltip key={j} arrow placement="top"
                                title={<Box component="img" src={cd.detail} alt={cn} sx={{ width: 180, borderRadius: '8px', display: 'block' }} />}
                                componentsProps={{ tooltip: { sx: { bgcolor: 'rgba(17,24,39,0.97)', border: '1px solid rgba(76,29,149,0.4)', p: 0.75 } } }}>
                                <Box component="img" src={cd.thumbnail} alt={cn} sx={{
                                    height: TURN_CARD_H, width: TURN_CARD_W,
                                    borderRadius: '4px', objectFit: 'cover', flexShrink: 0,
                                    border: '1.5px solid rgba(124,58,237,0.35)',
                                    transition: 'transform 0.12s, border-color 0.12s',
                                    cursor: 'pointer',
                                    '&:hover': { transform: 'scale(1.06)', borderColor: 'rgba(124,58,237,0.7)' }
                                }} />
                            </Tooltip>
                        ) : null;
                    }) : <Box sx={{ width: TURN_CARD_W }} />}
                </Box>
            </TableCell>
            {/* Action text */}
            <TableCell sx={{ py: 1, px: 1.5, borderBottom: cellBorder, verticalAlign: 'middle' }}>
                <Typography sx={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                    {row.action}
                </Typography>
            </TableCell>
        </TableRow>
    );
}

function TurnSequenceTable({ turns, getCard }) {
    if (!turns?.length) return null;
    const maxCards = Math.max(...turns.map(r => r.card_names?.filter(Boolean).length || 0), 1);
    const cardsColWidth = maxCards * (TURN_CARD_W + 4) + 20;
    return (
        <TableContainer sx={{ borderRadius: '8px', border: '1px solid rgba(76,29,149,0.2)', overflow: 'hidden' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableBody>
                    {turns.map((row, i) => (
                        <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? 'rgba(17,24,39,0.85)' : 'rgba(30,27,75,0.25)' }}>
                            <TurnRow row={row} getCard={getCard} cardsColWidth={cardsColWidth} />
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function PlayLineBlock({ pl, getCard, archetypeLookup }) {
    const [side, setSide] = useState(0);
    const ts = pl.turn_sequence;
    const sides = [];
    if (ts?.otp?.length) sides.push({ label: 'On the Play', key: 'otp', data: ts.otp });
    if (ts?.otd?.length) sides.push({ label: 'On the Draw', key: 'otd', data: ts.otd });

    const hasLeft = pl.bullet_points?.length > 0 || pl.ideal_for?.length > 0;
    const hasRight = sides.length > 0;

    return (
        <Box sx={{ bgcolor: 'rgba(0,0,0,0.18)', borderRadius: '10px', p: { xs: 1.5, sm: 2 }, border: '1px solid rgba(76,29,149,0.15)' }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: hasLeft && hasRight ? { xs: '1fr', sm: '2fr 3fr' } : '1fr',
                gap: { xs: 1.5, sm: 2.5 },
                alignItems: 'start',
            }}>
                {/* Left: bullet points + ideal_for */}
                {hasLeft && (
                    <Box>
                        {pl.bullet_points?.length > 0 && (
                            <Box sx={{ mb: pl.ideal_for?.length ? 1.75 : 0 }}>
                                {pl.bullet_points.map((bp, i) => (
                                    <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.6 }}>
                                        <Typography sx={{ color: '#7c3aed', fontSize: '0.9rem', lineHeight: 1.6, flexShrink: 0 }}>•</Typography>
                                        <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.82)', lineHeight: 1.6 }}>{bp}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {pl.ideal_for?.length > 0 && (
                            <Box>
                                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                                    Idealne na
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {pl.ideal_for.map((item, i) => {
                                        const name = item?.name || item;
                                        const colors = item?.ink_colors?.length
                                            ? item.ink_colors
                                            : (() => {
                                                const lower = (name || '').toLowerCase();
                                                const found = Object.values(archetypeLookup || {}).find(a => a.name?.toLowerCase() === lower);
                                                return found?.ink_colors || [];
                                            })();
                                        const side = item?.side;
                                        const sideCfg = PLAY_LINE_SIDE[side];
                                        return (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, width: 'fit-content' }}>
                                                <Box sx={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                                                    px: 1, py: 0.4,
                                                    bgcolor: 'rgba(124,58,237,0.12)',
                                                    border: '1px solid rgba(124,58,237,0.25)',
                                                    borderRadius: '6px',
                                                }}>
                                                    {colors.length > 0 && <InkColorIcons colors={colors} size={14} gap={0.2} tooltip={false} />}
                                                    <Typography sx={{ fontSize: '0.78rem', color: '#c4b5fd', fontWeight: 500 }}>{name}</Typography>
                                                </Box>
                                                {sideCfg && (
                                                    <Box sx={{ px: 0.6, py: 0.15, borderRadius: '4px', bgcolor: sideCfg.bg, border: `1px solid ${sideCfg.border}` }}>
                                                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: sideCfg.color, lineHeight: 1.4 }}>{sideCfg.label}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Right: turn sequence with OTP/OTD toggle */}
                {hasRight && (
                    <Box>
                        <OtpOtdToggle sides={sides} active={side} onChange={setSide} />
                        {sides.length === 1 && (
                            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                                {sides[0].label}
                            </Typography>
                        )}
                        <TurnSequenceTable turns={sides[side]?.data} getCard={getCard} />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function GameplanSection({ gameplan, getCard, archetypeLookup }) {
    const [plTab, setPlTab] = useState(0);
    if (!gameplan) return null;
    const hasLines = gameplan.play_lines?.length > 0;
    return (
        <Box sx={sectionBox}>
            <SectionHeader id="guide-gameplan">Gameplan</SectionHeader>
            {gameplan.text && (
                <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.82)', lineHeight: 1.8, mb: hasLines ? 2.5 : 0, whiteSpace: 'pre-line' }}>
                    {gameplan.text}
                </Typography>
            )}
            {hasLines && (
                <>
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                        {gameplan.play_lines.map((pl, i) => (
                            <Box key={i} onClick={() => setPlTab(i)} sx={{
                                px: 1.25, py: 0.6, borderRadius: '7px', cursor: 'pointer',
                                bgcolor: plTab === i ? 'rgba(124,58,237,0.28)' : 'rgba(0,0,0,0.2)',
                                border: `1px solid ${plTab === i ? 'rgba(124,58,237,0.55)' : 'rgba(76,29,149,0.2)'}`,
                                transition: 'all 0.15s',
                            }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: plTab === i ? 700 : 400, color: plTab === i ? '#c4b5fd' : 'rgba(196,181,253,0.5)' }}>
                                    {pl.title}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <PlayLineBlock pl={gameplan.play_lines[plTab]} getCard={getCard} archetypeLookup={archetypeLookup} />
                </>
            )}
        </Box>
    );
}

// ─── Mulligan ─────────────────────────────────────────────────────────────────

function MulliganHandCarousel({ hands, getCard }) {
    const [idx, setIdx] = useState(0);
    const total = hands.length;
    const h = hands[idx] || {};

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Przykładowe ręce
                </Typography>
                {total > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box onClick={() => setIdx((idx - 1 + total) % total)} sx={{
                            width: 24, height: 24, borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', border: '1px solid rgba(76,29,149,0.3)', color: 'rgba(196,181,253,0.5)',
                            '&:hover': { borderColor: 'rgba(124,58,237,0.5)', color: '#c4b5fd', bgcolor: 'rgba(124,58,237,0.1)' },
                            transition: 'all 0.12s', userSelect: 'none', fontSize: '0.85rem', fontWeight: 700,
                        }}>‹</Box>
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(196,181,253,0.55)', minWidth: 32, textAlign: 'center' }}>
                            {idx + 1}/{total}
                        </Typography>
                        <Box onClick={() => setIdx((idx + 1) % total)} sx={{
                            width: 24, height: 24, borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', border: '1px solid rgba(76,29,149,0.3)', color: 'rgba(196,181,253,0.5)',
                            '&:hover': { borderColor: 'rgba(124,58,237,0.5)', color: '#c4b5fd', bgcolor: 'rgba(124,58,237,0.1)' },
                            transition: 'all 0.12s', userSelect: 'none', fontSize: '0.85rem', fontWeight: 700,
                        }}>›</Box>
                    </Box>
                )}
            </Box>
            <MulliganHand cardNames={h.card_names} mulliganIndices={h.mulligan_indices} description={h.description} getCard={getCard} />
            {total > 1 && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 1.25, justifyContent: 'center' }}>
                    {hands.map((_, i) => (
                        <Box key={i} onClick={() => setIdx(i)} sx={{
                            width: i === idx ? 16 : 6, height: 6,
                            borderRadius: '3px',
                            bgcolor: i === idx ? '#7c3aed' : 'rgba(124,58,237,0.25)',
                            cursor: 'pointer', transition: 'all 0.2s',
                            '&:hover': { bgcolor: i === idx ? '#7c3aed' : 'rgba(124,58,237,0.5)' },
                        }} />
                    ))}
                </Box>
            )}
        </Box>
    );
}

const MULLIGAN_SIDES = [
    { key: 'otp', label: 'On the Play' },
    { key: 'otd', label: 'On the Draw' },
];

function MulliganHand({ cardNames = [], mulliganIndices = [], description, getCard }) {
    return (
        <Box>
            <Box sx={{
                display: 'flex', gap: { xs: 0.5, sm: 0.75 }, flexWrap: 'wrap',
                px: 0.25, pt: 0.25, pb: 0.5,
            }}>
                {cardNames.map((name, i) => {
                    const discard = mulliganIndices.includes(i);
                    return (
                        <CardImage
                            key={i}
                            name={name}
                            cardData={getCard(name)}
                            width={CARD_W_MD}
                            height={CARD_H_MD}
                            dimmed={discard}
                            showX={discard}
                        />
                    );
                })}
            </Box>
            {description && (
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(196,181,253,0.75)', fontStyle: 'italic', mt: 0.75, lineHeight: 1.55 }}>
                    {description}
                </Typography>
            )}
        </Box>
    );
}

function MulliganSide({ data, getCard }) {
    if (!data) return null;
    const priorities = data.priorities || [];
    const hasPriorities = priorities.length > 0;
    const hasHands = data.example_hands?.length > 0;
    if (!hasPriorities && !hasHands) return null;
    const isCardPriorities = hasPriorities && typeof priorities[0] === 'object';

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: hasPriorities && hasHands ? { xs: '1fr', sm: '2fr 3fr' } : '1fr',
            gap: { xs: 2, sm: 2.5 },
            alignItems: 'start',
        }}>
            {hasPriorities && (
                <Box>
                    <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                        Priorytety mulliganu
                    </Typography>
                    {isCardPriorities ? (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {priorities.map((p, i) => {
                                const name = p.card_name || '';
                                return <KeyCardThumb key={i} cardName={name} cardData={getCard(name)} />;
                            })}
                        </Box>
                    ) : (
                        priorities.map((p, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.6 }}>
                                <Box sx={{ mt: 0.15, width: 20, height: 20, borderRadius: '50%', bgcolor: 'rgba(124,58,237,0.2)', border: '1px solid rgba(124,58,237,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#7c3aed', lineHeight: 1 }}>{i + 1}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.55, pt: 0.1 }}>{p}</Typography>
                            </Box>
                        ))
                    )}
                </Box>
            )}

            {hasHands && (
                <MulliganHandCarousel hands={data.example_hands} getCard={getCard} />
            )}
        </Box>
    );
}

function MulliganSection({ mulligan, getCard }) {
    const [side, setSide] = useState(0);
    if (!mulligan) return null;
    const isUnified = mulligan.unified !== false;
    const hasBoth = !isUnified && hasMulliganContent(mulligan.otp) && hasMulliganContent(mulligan.otd);
    const activeData = hasBoth ? (side === 0 ? mulligan.otp : mulligan.otd) : (mulligan.otp || mulligan.otd);
    if (!hasMulliganContent(activeData) && !mulligan.general_notes) return null;
    return (
        <Box sx={sectionBox}>
            <SectionHeader id="guide-mulligan">Mulligan</SectionHeader>
            {mulligan.general_notes && (
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(196,181,253,0.7)', mb: 2, fontStyle: 'italic', lineHeight: 1.65 }}>
                    {mulligan.general_notes}
                </Typography>
            )}
            {hasBoth && (
                <Box sx={{ mb: 2 }}>
                    <OtpOtdToggle sides={MULLIGAN_SIDES} active={side} onChange={setSide} />
                </Box>
            )}
            <MulliganSide data={activeData} getCard={getCard} />
        </Box>
    );
}

// ─── Matchups ─────────────────────────────────────────────────────────────────

function FavorabilityBar({ value }) {
    const cfg = FAVORABILITY[value];
    if (!cfg) return null;
    const pct = cfg.bar;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ flex: 1, height: 6, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{
                    height: '100%', borderRadius: 3,
                    width: `${pct}%`,
                    bgcolor: cfg.color,
                    transition: 'width 0.4s ease',
                    boxShadow: `0 0 8px ${cfg.color}60`
                }} />
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: cfg.color, fontWeight: 700, flexShrink: 0, minWidth: 24 }}>
                {pct}%
            </Typography>
        </Box>
    );
}

function PriorityCardList({ priorities, getCard }) {
    if (!priorities?.length) return null;
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
            {priorities.map((p, i) => {
                const name = typeof p === 'string' ? p : (p.card_name || '');
                const isCard = typeof p === 'object';
                if (isCard) return <KeyCardThumb key={i} cardName={name} cardData={getCard(name)} compact />;
                return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25,
                        bgcolor: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: '5px' }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#7c3aed', minWidth: 14 }}>{i + 1}.</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)' }}>{name}</Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

function MatchupMulligan({ otpData, otdData, getCard }) {
    const [side, setSide] = useState(0);
    const hasBoth = !!(otpData && otdData &&
        (hasMulliganContent(otpData) || hasMulliganContent(otdData)));
    const active = side === 0 ? (otpData || otdData) : (otdData || otpData);
    if (!active || (!hasMulliganContent(otpData) && !hasMulliganContent(otdData))) return null;

    const priorities = active?.priorities || [];
    const hasHands = active?.example_hands?.length > 0;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Mulligan
                </Typography>
                {hasBoth && (
                    <Box sx={{ display: 'flex', gap: 0.4 }}>
                        {MULLIGAN_SIDES.map(({ key, label }, i) => (
                            <Box key={key} onClick={() => setSide(i)} sx={{
                                px: 1, py: 0.2, borderRadius: '5px', cursor: 'pointer',
                                bgcolor: side === i ? 'rgba(124,58,237,0.25)' : 'transparent',
                                border: `1px solid ${side === i ? 'rgba(124,58,237,0.5)' : 'rgba(76,29,149,0.15)'}`,
                                transition: 'all 0.15s',
                            }}>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: side === i ? 700 : 400, color: side === i ? '#c4b5fd' : 'rgba(196,181,253,0.4)' }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
            <PriorityCardList priorities={priorities} getCard={getCard} />
            {hasHands && <MulliganHandCarousel hands={active.example_hands} getCard={getCard} />}
        </Box>
    );
}

function MatchupTurnBlock({ otpTurns, otdTurns, getCard }) {
    const [side, setSide] = useState(0);
    const hasBoth = otpTurns?.length > 0 && otdTurns?.length > 0;
    const active = side === 0 ? (otpTurns?.length ? otpTurns : otdTurns) : (otdTurns?.length ? otdTurns : otpTurns);
    if (!active?.length) return null;
    return (
        <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Sekwencja tur
                </Typography>
                {hasBoth ? (
                    <Box sx={{ display: 'flex', gap: 0.4 }}>
                        {MULLIGAN_SIDES.map(({ key, label }, i) => (
                            <Box key={key} onClick={() => setSide(i)} sx={{
                                px: 1, py: 0.2, borderRadius: '5px', cursor: 'pointer',
                                bgcolor: side === i ? 'rgba(124,58,237,0.25)' : 'transparent',
                                border: `1px solid ${side === i ? 'rgba(124,58,237,0.5)' : 'rgba(76,29,149,0.15)'}`,
                                transition: 'all 0.15s',
                            }}>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: side === i ? 700 : 400, color: side === i ? '#c4b5fd' : 'rgba(196,181,253,0.4)' }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)' }}>
                        {otpTurns?.length ? 'On the Play' : 'On the Draw'}
                    </Typography>
                )}
            </Box>
            <TurnSequenceTable turns={active} getCard={getCard} />
        </Box>
    );
}

const FAV_ORDER = { very_favored: 0, favored: 1, even: 2, unfavored: 3, very_unfavored: 4 };

function ReplayLinksDisplay({ replays }) {
    const valid = (replays || []).filter((r) => r.url);
    if (!valid.length) return null;
    return (
        <Box sx={{ mt: 1.75 }}>
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                Powtórki
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {valid.map((r, i) => (
                    <Box
                        key={i}
                        component="a"
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            px: 1.25, py: 0.5,
                            bgcolor: 'rgba(124,58,237,0.12)',
                            border: '1px solid rgba(124,58,237,0.25)',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            color: '#c4b5fd',
                            fontSize: '0.82rem',
                            fontWeight: 500,
                            transition: 'all 0.15s',
                            '&:hover': {
                                bgcolor: 'rgba(124,58,237,0.22)',
                                borderColor: 'rgba(124,58,237,0.5)',
                                color: 'white',
                            },
                        }}
                    >
                        <PlayCircleOutlineIcon sx={{ fontSize: 15 }} />
                        {r.label || `Replay ${i + 1}`}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function MatchupsSection({ matchups, vsArchetypes, getCard, archetypeLookup }) {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(() => new Set([0]));

    if (!matchups?.length) return null;
    const sorted = [...matchups].sort((a, b) => (FAV_ORDER[a.favorability] ?? 9) - (FAV_ORDER[b.favorability] ?? 9));
    const allExpanded = expanded.size >= sorted.length;

    const toggleExpanded = (i) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i); else next.add(i);
        return next;
    });

    const toggleAll = () => {
        if (allExpanded) setExpanded(new Set([0]));
        else setExpanded(new Set(sorted.map((_, i) => i)));
    };

    return (
        <Box sx={sectionBox}>
            <SectionHeader id="guide-matchups" action={
                <Box onClick={toggleAll} sx={{
                    display: 'flex', alignItems: 'center', px: 1, py: 0.3, borderRadius: '6px',
                    cursor: 'pointer', border: '1px solid rgba(76,29,149,0.25)',
                    color: 'rgba(196,181,253,0.5)',
                    '&:hover': { bgcolor: 'rgba(124,58,237,0.1)', color: '#c4b5fd' },
                    transition: 'all 0.15s',
                }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                        {allExpanded ? 'Zwiń wszystko' : 'Rozwiń wszystko'}
                    </Typography>
                </Box>
            }>Matchupy</SectionHeader>
            {sorted.map((m, i) => {
                const cfg = FAVORABILITY[m.favorability];
                const tracker = vsArchetypes?.[m.archetype_id];
                const tGames = tracker?.games || 0;
                const tWR = tracker?.winrate;
                const showWR = tGames >= 10;
                const archInfo = archetypeLookup?.[m.archetype_id] || {};
                const inkColors = m.ink_colors?.length ? m.ink_colors : (archInfo.ink_colors || []);
                return (
                    <Accordion key={i} disableGutters expanded={expanded.has(i)} onChange={() => toggleExpanded(i)} sx={{
                        background: 'rgba(17,24,39,0.5)',
                        border: `1px solid ${cfg?.border || 'rgba(76,29,149,0.2)'}`,
                        borderRadius: '10px !important', mb: 1.25, overflow: 'hidden',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { border: `1px solid ${cfg?.color || 'rgba(124,58,237,0.4)'}50` }
                    }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(196,181,253,0.55)', fontSize: 22 }} />}
                            sx={{ minHeight: 56, px: 2, '& .MuiAccordionSummary-content': { my: 0.75 } }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                    {inkColors.length > 0 && (
                                        <InkColorIcons colors={inkColors} size={22} gap={0.35} tooltip={false} />
                                    )}
                                    <Typography
                                        onClick={(e) => { if (m.archetype_id) { e.stopPropagation(); navigate(`/archetype/${m.archetype_id}`); } }}
                                        sx={{ fontSize: '0.92rem', fontWeight: 700, color: 'white', cursor: m.archetype_id ? 'pointer' : 'default', '&:hover': m.archetype_id ? { color: '#c4b5fd', textDecoration: 'underline' } : {} }}
                                    >
                                        {m.archetype_name || m.archetype_id}
                                    </Typography>
                                    {cfg && (
                                        <Chip label={cfg.label} size="small" sx={{
                                            bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                                            fontWeight: 600, fontSize: '0.68rem', height: 22,
                                            '& .MuiChip-label': { px: 0.85 }
                                        }} />
                                    )}
                                    {showWR ? (
                                        <Typography sx={{ fontSize: '0.72rem', color: tWR >= 60 ? '#10b981' : tWR >= 50 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                                            {tWR}% ({tGames} meczów)
                                        </Typography>
                                    ) : tGames > 0 ? (
                                        <Typography sx={{ fontSize: '0.68rem', color: 'rgba(156,163,175,0.6)' }}>za mało danych</Typography>
                                    ) : null}
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0.5, borderTop: `1px solid ${cfg?.border || 'rgba(76,29,149,0.15)'}` }}>
                            {(() => {
                                const hasLeft = !!(m.gameplan || m.tips?.length > 0);
                                const hasMulligan = hasMulliganContent(m.mulligan_otp) || hasMulliganContent(m.mulligan_otd);
                                const hasRight = !!(m.key_cards?.length > 0 || m.threats?.length > 0);
                                if (!hasLeft && !hasRight && !hasMulligan) return null;
                                return (
                                    <>
                                        {/* Two-column grid: gameplan + key cards/threats */}
                                        {(hasLeft || hasRight) && (
                                            <Box sx={{
                                                display: 'grid',
                                                gridTemplateColumns: hasLeft && hasRight ? { xs: '1fr', md: '5fr 4fr' } : '1fr',
                                                gap: { xs: 2, md: 3 },
                                                alignItems: 'start',
                                                mb: hasMulligan ? 2 : 0,
                                            }}>
                                                {/* Left: gameplan + tips */}
                                                {hasLeft && (
                                                    <Box>
                                                        {m.gameplan && (
                                                            <Typography sx={{ fontSize: '0.87rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, mb: m.tips?.length ? 1.75 : 0, whiteSpace: 'pre-line' }}>
                                                                {m.gameplan}
                                                            </Typography>
                                                        )}
                                                        {m.tips?.length > 0 && (
                                                            <Box>
                                                                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                                                                    Tipy
                                                                </Typography>
                                                                {m.tips.map((tip, j) => (
                                                                    <Box key={j} sx={{ display: 'flex', gap: 1, mb: 0.6 }}>
                                                                        <Typography sx={{ color: '#7c3aed', fontSize: '0.85rem', lineHeight: 1.65, flexShrink: 0 }}>•</Typography>
                                                                        <Typography sx={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.65 }}>{tip}</Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                                {/* Right: key cards + threats */}
                                                {hasRight && (
                                                    <Box>
                                                        {m.key_cards?.length > 0 && (
                                                            <Box sx={{ mb: m.threats?.length ? 1.5 : 0 }}>
                                                                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(196,181,253,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                                                                    Kluczowe karty
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                                                    {m.key_cards.map((c, j) => (
                                                                        <KeyCardThumb key={j} cardName={c.card_name || c} cardData={getCard(c.card_name || c)} compact />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                        {m.threats?.length > 0 && (
                                                            <Box>
                                                                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(239,68,68,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                                                                    Uważaj na
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                                                    {m.threats.map((c, j) => (
                                                                        <KeyCardThumb key={j} cardName={c.card_name || c} cardData={getCard(c.card_name || c)} compact threat />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                        {/* Full-width mulligan tile */}
                                        {hasMulligan && (
                                            <Box sx={{
                                                bgcolor: 'rgba(0,0,0,0.18)',
                                                border: '1px solid rgba(76,29,149,0.18)',
                                                borderRadius: '8px',
                                                p: 1.5,
                                            }}>
                                                <MatchupMulligan otpData={m.mulligan_otp} otdData={m.mulligan_otd} getCard={getCard} />
                                            </Box>
                                        )}
                                    </>
                                );
                            })()}
                            <ReplayLinksDisplay replays={m.replays} />
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
}

// ─── Alternative Builds ───────────────────────────────────────────────────────

function DeckMiniThumbnail({ deck }) {
    const cards = deck?.deckVisual?.cards || [];
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '2px', p: '4px' }}>
            {cards.map((card, i) => {
                const img = card?.image?.thumbnail || card?.image?.detail;
                return (
                    <Box key={i} sx={{ aspectRatio: '4/3', borderRadius: '3px', overflow: 'hidden', bgcolor: 'rgba(76,29,149,0.2)' }}>
                        {img && <img src={img} alt={card.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />}
                    </Box>
                );
            })}
            {cards.length === 0 && Array.from({ length: 12 }).map((_, i) => (
                <Box key={i} sx={{ aspectRatio: '4/3', borderRadius: '3px', bgcolor: 'rgba(76,29,149,0.12)' }} />
            ))}
        </Box>
    );
}

function AltBuildCard({ build }) {
    const { deck, loading } = useDeck(build.deck_id);
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const inkColors = deck?.archetype?.ink_colors || deck?.archetypeInfo?.ink_colors || [];
    const deckName = deck?.name;

    return (
        <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: '10px', border: '1px solid rgba(76,29,149,0.18)', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Deck header — ink colors + name + link */}
            {build.deck_id && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    {loading
                        ? <Skeleton variant="rounded" width={120} height={22} sx={{ bgcolor: 'rgba(76,29,149,0.12)' }} />
                        : <>
                            {inkColors.length > 0 && <InkColorIcons colors={inkColors} size={15} gap={0.25} />}
                            <Tooltip
                                arrow
                                placement="top"
                                title={deck ? (
                                    <Box sx={{ p: 0.25 }}>
                                        <DeckMiniThumbnail deck={deck} />
                                        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(196,181,253,0.8)', textAlign: 'center', mt: 0.25 }}>
                                            {deckName}
                                        </Typography>
                                    </Box>
                                ) : ''}
                                componentsProps={{ tooltip: { sx: { bgcolor: 'rgba(17,24,39,0.97)', border: '1px solid rgba(76,29,149,0.4)', p: 0.5, maxWidth: 260 } } }}
                            >
                                <Typography
                                    onClick={() => navigate(`/tracker/${build.deck_id}`)}
                                    sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#c4b5fd', cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, lineHeight: 1.3 }}
                                >
                                    {deckName || build.title || '—'}
                                </Typography>
                            </Tooltip>
                        </>
                    }
                    <DeckPreviewModal open={modalOpen} onClose={() => setModalOpen(false)} deck={deck} />
                </Box>
            )}
            {!build.deck_id && build.title && (
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#c4b5fd', lineHeight: 1.3 }}>{build.title}</Typography>
            )}

            {build.when_to_consider && (
                <Typography sx={{ fontSize: '0.8rem', color: '#f59e0b', fontStyle: 'italic', lineHeight: 1.55 }}>
                    Kiedy rozważyć: {build.when_to_consider}
                </Typography>
            )}

            {build.description && (
                <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, whiteSpace: 'pre-line' }}>
                    {build.description}
                </Typography>
            )}
        </Box>
    );
}

function AlternativeBuildsSection({ alternativeBuilds }) {
    if (!alternativeBuilds?.length) return null;
    return (
        <Box sx={sectionBox}>
            <SectionHeader id="guide-builds">Alternatywne buildy</SectionHeader>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 1.5,
            }}>
                {alternativeBuilds.map((b, i) => (
                    <AltBuildCard key={i} build={b} />
                ))}
            </Box>
        </Box>
    );
}

// ─── Sticky section navigation ───────────────────────────────────────────────

const GUIDE_NAV_ITEMS = [
    { id: 'guide-tldr',     label: 'TL;DR' },
    { id: 'guide-cards',    label: 'Karty' },
    { id: 'guide-gameplan', label: 'Gameplan' },
    { id: 'guide-mulligan', label: 'Mulligan' },
    { id: 'guide-matchups', label: 'Matchupy' },
    { id: 'guide-builds',   label: 'Buildy' },
];

function GuideStickyNav({ visibleSections }) {
    const [active, setActive] = useState('guide-tldr');

    useEffect(() => {
        const targets = GUIDE_NAV_ITEMS.map(({ id }) => document.getElementById(id)).filter(Boolean);
        if (!targets.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter((e) => e.isIntersecting);
                if (visible.length) {
                    const topmost = visible.reduce((a, b) =>
                        a.boundingClientRect.top < b.boundingClientRect.top ? a : b
                    );
                    setActive(topmost.target.id);
                }
            },
            { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
        );

        targets.forEach((t) => observer.observe(t));
        return () => observer.disconnect();
    }, []);

    const shown = GUIDE_NAV_ITEMS.filter(({ id }) => visibleSections.includes(id));
    if (shown.length < 2) return null;

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const offset = 112; // site header (~64px) + guide sticky nav (~40px) + margin
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
    };

    return (
        <Box sx={{
            position: 'sticky', top: 0, zIndex: 10, mb: 2,
            mx: { xs: -0.5, sm: 0 },
        }}>
            <Box sx={{
                display: 'flex', gap: 0.5, overflowX: 'auto', flexWrap: 'nowrap',
                px: { xs: 0.5, sm: 0 }, py: 0.75,
                background: 'rgba(15,23,42,0.88)', backdropFilter: 'blur(12px)',
                borderBottom: '1px solid rgba(76,29,149,0.2)',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
            }}>
                {shown.map(({ id, label }) => {
                    const isActive = active === id;
                    return (
                        <Box key={id} onClick={() => scrollTo(id)} sx={{
                            px: 1.5, py: 0.5, borderRadius: '6px', cursor: 'pointer', flexShrink: 0,
                            bgcolor: isActive ? 'rgba(124,58,237,0.3)' : 'transparent',
                            border: `1px solid ${isActive ? 'rgba(124,58,237,0.55)' : 'transparent'}`,
                            transition: 'all 0.15s',
                            '&:hover': { bgcolor: isActive ? 'rgba(124,58,237,0.3)' : 'rgba(124,58,237,0.1)' },
                        }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#c4b5fd' : 'rgba(196,181,253,0.55)', whiteSpace: 'nowrap' }}>
                                {label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function GuideSkeleton() {
    return (
        <Box>
            {[120, 200, 160, 240].map((h, i) => (
                <Skeleton key={i} variant="rounded" height={h} sx={{ mb: 2.5, bgcolor: 'rgba(76,29,149,0.12)', borderRadius: '12px' }} />
            ))}
        </Box>
    );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function GuideRenderer({ content, vsArchetypes, trackerFilters = {} }) {
    const { get: getCard, ready } = useCardLookup();
    const archetypeLookup = useArchetypeLookup(trackerFilters);

    if (!content || content.schema_version !== 1) return null;
    if (!ready) return <GuideSkeleton />;

    const visibleSections = [
        content.tldr && 'guide-tldr',
        content.card_breakdown?.length && 'guide-cards',
        content.gameplan && 'guide-gameplan',
        content.mulligan && 'guide-mulligan',
        content.matchups?.length && 'guide-matchups',
        content.alternative_builds?.length && 'guide-builds',
    ].filter(Boolean);

    return (
        <Box>
            <GuideStickyNav visibleSections={visibleSections} />
            <TldrSection
                tldr={content.tldr}
                matchups={content.matchups}
                archetypeLookup={archetypeLookup}
                deckId={content.meta?.deck_list_id}
                getCard={getCard}
            />
            <CardBreakdownSection cardBreakdown={content.card_breakdown} getCard={getCard} />
            <GameplanSection gameplan={content.gameplan} getCard={getCard} archetypeLookup={archetypeLookup} />
            <MulliganSection mulligan={content.mulligan} getCard={getCard} />
            <MatchupsSection matchups={content.matchups} vsArchetypes={vsArchetypes} getCard={getCard} archetypeLookup={archetypeLookup} />
            <AlternativeBuildsSection alternativeBuilds={content.alternative_builds} />
        </Box>
    );
}
